import { useState } from 'preact/hooks';
import {
  chatDisplayName,
  chats,
  connection,
  deleteChat,
  openChat,
  session,
  unreadCount,
} from '../lib/engine.js';
import { go } from './nav.js';
import { Avatar } from './Avatar.js';
import { ConnectionBar } from './ConnectionBar.js';
import { PushPrompt } from './PushPrompt.js';
import { useSwipeToReveal } from './useSwipeToReveal.js';
import type { StoredChat } from '../lib/store.js';

/** Ширина кнопки под строкой. Совпадает с --swipe-action-width в стилях. */
const ACTION_WIDTH = 88;

export function ChatList() {
  /*
   * Открытая строка ровно одна. Иначе после нескольких свайпов список
   * оказывается наполовину разъехавшимся, и непонятно, к чему относится
   * следующее нажатие.
   */
  const [revealedId, setRevealedId] = useState<string | null>(null);

  return (
    <div class="screen">
      <header class="topbar">
        <h1>Чаты</h1>
        <button class="icon-btn" onClick={() => go('new-chat')} title="Новый чат" aria-label="Новый чат">
          ✚
        </button>
        <button class="icon-btn" onClick={() => go('settings')} title="Настройки" aria-label="Настройки">
          ⚙
        </button>
      </header>

      <ConnectionBar />

      <div class="scroll" onScroll={() => setRevealedId(null)}>
        <PushPrompt />

        {chats.value.length === 0 ? (
          <div class="empty">
            Пока пусто.
            <br />
            Нажми ✚ и напиши первому собеседнику по его имени пользователя.
          </div>
        ) : (
          chats.value.map((chat) => (
            <ChatRow
              key={chat.id}
              chat={chat}
              revealed={revealedId === chat.id}
              onReveal={(open) => setRevealedId(open ? chat.id : null)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ChatRow({
  chat,
  revealed,
  onReveal,
}: {
  chat: StoredChat;
  revealed: boolean;
  onReveal: (open: boolean) => void;
}) {
  const name = chatDisplayName(chat);
  const unread = unreadCount(chat);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const { shift, shouldSuppressClick, handlers } = useSwipeToReveal(ACTION_WIDTH, revealed, (open) => {
    onReveal(open);
    if (!open) setConfirming(false);
  });

  const open = async () => {
    // Нажатие сразу после жеста гасим: палец целился в кнопку удаления,
    // а не в переписку.
    if (shouldSuppressClick()) {
      onReveal(false);
      setConfirming(false);
      return;
    }
    await openChat(chat.id);
    go('chat');
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteChat(chat.id);
    } catch (error) {
      console.error('не удалось удалить чат:', error);
      setBusy(false);
      setConfirming(false);
      onReveal(false);
    }
  };

  // У группы своей аватарки нет — показываем букву названия. У личного чата
  // берём собеседника, а не себя.
  const other = chat.members.find((member) => member.id !== session.value?.userId);

  if (confirming) {
    return (
      <div class="chat-row-confirm">
        <div class="body">
          <div class="name">Удалить «{name}»?</div>
          <div class="preview">
            Чат исчезнет только у тебя. У собеседника переписка останется.
          </div>
        </div>
        <button class="btn danger" disabled={busy} onClick={remove}>
          {busy ? '…' : 'Удалить'}
        </button>
        <button
          class="btn ghost"
          disabled={busy}
          onClick={() => {
            setConfirming(false);
            onReveal(false);
          }}
        >
          Отмена
        </button>
      </div>
    );
  }

  return (
    <div class="chat-row-swipe">
      <button
        class="chat-row-action"
        aria-label={`Удалить чат «${name}»`}
        tabIndex={revealed ? 0 : -1}
        onClick={() => setConfirming(true)}
      >
        Удалить
      </button>

      <button
        class="chat-row"
        style={shift === 0 ? undefined : `transform: translateX(${shift}px)`}
        onClick={open}
        {...handlers}
      >
        {chat.kind === 'direct' && other ? (
          <Avatar userId={other.id} name={name} />
        ) : (
          <div class="avatar" aria-hidden="true">
            {name.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div class="body">
          <div class="name">{name}</div>
          <div class="preview">
            {chat.preview ?? (chat.lastSeq > 0 ? 'Сообщение не расшифровано' : 'Нет сообщений')}
          </div>
        </div>
        {unread > 0 && connection.value !== 'connecting' && (
          <span class="badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
    </div>
  );
}
